import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import css from '../../../ForYouSection/ForYouSection.module.css';
import modalCss from './PersonalOfferModal.module.css';
import { getEmailBodyJS } from '../../../ForYouSection/emailPayload';
import { sendCustomEmailJS } from '../../../../../../util/sendEmails';
import { IconClose } from '../../../../../../components';

const getLanguageName = locale => {
  const languages = {
    de: 'German',
    en: 'English',
  };
  return languages[locale] || locale;
};

const PersonalOfferModal = ({ isOpen, onClose }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formValues, setFormValues] = useState({
    destination: '',
    dates: '',
    email: '',
    name: '',
    budget: '',
    next4weeks: '',
  });
  const [errors, setErrors] = useState({});
  const intl = useIntl();

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close modal on Escape key press
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = e => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const validateEmail = email => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const newErrors = {};

    if (!formValues.destination) {
      newErrors.destination = <FormattedMessage id="foryousection.destinationError" />;
    }
    if (!formValues.dates) {
      newErrors.dates = <FormattedMessage id="foryousection.datesError" />;
    }
    if (!formValues.budget) {
      newErrors.budget = <FormattedMessage id="foryousection.budgetError" />;
    }
    if (!formValues.next4weeks) {
      newErrors.next4weeks = <FormattedMessage id="foryousection.next4weeksError" />;
    }
    if (!formValues.name) {
      newErrors.name = <FormattedMessage id="foryousection.nameError" />;
    }
    if (!formValues.email) {
      newErrors.email = <FormattedMessage id="foryousection.emailError" />;
    } else if (!validateEmail(formValues.email)) {
      newErrors.email = <FormattedMessage id="foryousection.emailErrorInvalid" />;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      let emailBody = getEmailBodyJS({
        ...formValues,
        userLanguage: getLanguageName(intl?.locale?.trim() ?? 'de'),
      });

      setErrors({});
      await sendCustomEmailJS(emailBody);
      setIsSubmitted(true);

      // Reset form after 5 seconds
      setTimeout(() => {
        setFormValues({
          destination: '',
          dates: '',
          email: '',
          name: '',
          budget: '',
          next4weeks: '',
        });
        setIsSubmitted(false);
      }, 5000);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className={modalCss.modalOverlay} onClick={onClose}>
      <div className={modalCss.modalContainer} onClick={e => e.stopPropagation()}>
        <div>
          <div className={css.formWrapper}>
            <button
              className={modalCss.closeButton}
              onClick={onClose}
              type="button"
              aria-label="Close modal"
            >
              <IconClose rootClassName={modalCss.closeIcon} />
            </button>
            <form
              onSubmit={handleSubmit}
              className={classNames({ [css.disabledForm]: isSubmitted })}
            >
              <label className={css.inputLabel}>
                <span className={css.formLabel}>
                  <FormattedMessage id="foryousection.firstQuestion" />
                </span>
                <input
                  type="text"
                  name="destination"
                  placeholder={intl.formatMessage({ id: 'foryousection.interestPlaceholder' })}
                  className={css.formInput}
                  value={formValues.destination}
                  onChange={handleInputChange}
                  data-elbaction="click:click lp-foryou-regions"
                />
                {errors.destination && <div className={css.error}>{errors.destination}</div>}
              </label>
              <label className={css.inputLabel2}>
                <span className={css.formLabel}>
                  <FormattedMessage id="foryousection.thirdQuestion" />
                </span>
                <input
                  type="text"
                  name="dates"
                  placeholder={intl.formatMessage({ id: 'foryousection.datePlaceholder' })}
                  className={css.formInput}
                  value={formValues.dates}
                  onChange={handleInputChange}
                />
                {errors.dates && <div className={css.error}>{errors.dates}</div>}
              </label>
              <label className={css.inputLabel2}>
                <span className={css.formLabel}>
                  <FormattedMessage id="foryousection.budgetQuestion" />
                </span>
                <input
                  type="text"
                  name="budget"
                  placeholder={intl.formatMessage({ id: 'foryousection.budgetPlaceholder' })}
                  className={css.formInput}
                  value={formValues.budget}
                  onChange={handleInputChange}
                />
                {errors.budget && <div className={css.error}>{errors.budget}</div>}
              </label>
              <label className={css.inputLabel2}>
                <span className={css.formLabel}>
                  <FormattedMessage id="foryousection.next4weeksQuestion" />
                </span>
                <div className={css.checkboxGroup}>
                  <div className={css.checkboxWrapper}>
                    <input
                      type="radio"
                      id="next4weeks-yes"
                      name="next4weeks"
                      value="yes"
                      checked={formValues.next4weeks === 'yes'}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="next4weeks-yes">
                      <FormattedMessage id="FieldBoolean.yes" />
                    </label>
                  </div>
                  <div className={css.checkboxWrapper}>
                    <input
                      type="radio"
                      id="next4weeks-no"
                      name="next4weeks"
                      value="no"
                      checked={formValues.next4weeks === 'no'}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="next4weeks-no">
                      <FormattedMessage id="FieldBoolean.no" />
                    </label>
                  </div>
                </div>
                {errors.next4weeks && <div className={css.error}>{errors.next4weeks}</div>}
              </label>
              {/* name and email fields */}
              <div className={css.nameEmailWrapper}>
                <label className={css.inputLabel2}>
                  <span className={css.formLabel}>
                    <FormattedMessage id="foryousection.nameField" />
                  </span>
                  <input
                    type="text"
                    name="name"
                    placeholder={intl.formatMessage({ id: 'foryousection.namePlaceholder' })}
                    className={css.formInput}
                    value={formValues.name}
                    onChange={handleInputChange}
                    data-elbaction="click:click lp-foryou-name"
                  />
                  {errors.name && <div className={css.error}>{errors.name}</div>}
                </label>
                <label className={css.inputLabel2}>
                  <span className={css.formLabel}>
                    <FormattedMessage id="foryousection.emailInput" />
                  </span>
                  <input
                    type="email"
                    name="email"
                    placeholder={intl.formatMessage({ id: 'foryousection.emailPlaceholder' })}
                    className={css.formInput}
                    value={formValues.email}
                    onChange={handleInputChange}
                  />
                  {errors.email && <div className={css.error}>{errors.email}</div>}
                </label>
              </div>
              <button
                type="submit"
                className={css.submitBtn}
                data-elbaction="click:click lp-foryou-formsubmit"
              >
                <FormattedMessage id="foryousection.submitBtn" />
              </button>
            </form>
            <div className={classNames(css.formOverlay, { [css.formOverlayVisible]: isSubmitted })}>
              <div className={css.successMessage}>
                <div className={css.successIcon}>✓</div>
                <div className={css.successText}>
                  <FormattedMessage id="foryousection.submitSuccess" />
                </div>
                <div className={css.successSubtext}>
                  <FormattedMessage id="foryousection.submitSuccessSubtext" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
};

export default PersonalOfferModal;
